import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, switchMap, take } from 'rxjs';
import {
  selectCurrentUserRoles,
  selectLoading,
} from '../../state/current-user/current-user.selectors';
import { SETTLEMENT_ACCESS_ROLES } from '../shared/tab-access-roles';

const LEGACY_TAB_PATHS: Readonly<Record<string, string>> = {
  invoiceAndSettlement: 'settlement',
  recovery: 'recovery',
  uploads: 'uploads',
};

const resolveLandingTab = (
  requestedTabId: string | null,
  settlementVisible: boolean
): string => {
  const fallback = settlementVisible ? 'settlement' : 'recovery';
  if (requestedTabId === null) {
    return fallback;
  }
  const requestedPath = LEGACY_TAB_PATHS[requestedTabId];
  if (requestedPath === undefined) {
    return fallback;
  }
  if (requestedPath === 'settlement' && !settlementVisible) {
    return 'recovery';
  }
  return requestedPath;
};

const remainingQueryParams = (
  route: ActivatedRouteSnapshot
): Record<string, string> => {
  const params: Record<string, string> = {};
  for (const key of route.queryParamMap.keys) {
    if (key !== 'tabId') {
      const value = route.queryParamMap.get(key);
      if (value !== null) {
        params[key] = value;
      }
    }
  }
  return params;
};

export const claimLandingGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
) => {
  const store = inject(Store);
  const router = inject(Router);
  const claimId = route.paramMap.get('id');
  const requestedTabId = route.queryParamMap.get('tabId');
  const queryParams = remainingQueryParams(route);

  return store.select(selectLoading).pipe(
    filter((loading) => !loading),
    take(1),
    switchMap(() => store.select(selectCurrentUserRoles).pipe(take(1))),
    map((roles) => {
      const settlementVisible = roles.some(
        (role) => role.isActive && SETTLEMENT_ACCESS_ROLES.has(role.name)
      );
      const landingTab = resolveLandingTab(requestedTabId, settlementVisible);
      return router.createUrlTree(['/claims', claimId, landingTab], {
        queryParams,
      });
    })
  );
};
